import {
  startRegistration,
  startAuthentication,
  type RegistrationResponseJSON,
  type AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { Auth } from 'aws-amplify';
import { webAuthnService } from 'app/services/webauthn.service';
import {
  isMobileAppShell,
  isNativePasskeyAvailable,
  runNativePasskeyCeremony,
} from 'app/auth/nativePasskeyBridge';

/**
 * WebAuthn ceremonies for SEE-213 (browser) and SEE-214/SEE-217 (mobile shell).
 *
 * - Registration (create passkey) goes through the CDK v2-webauthn AppSync
 *   resolvers (webAuthnService): the user is already signed in.
 * - Login (returning user) goes through a Cognito CUSTOM_AUTH challenge via
 *   Amplify's Auth.signIn(username) with no password. The app client must have
 *   ALLOW_CUSTOM_AUTH enabled (wired by the Amplify auth override).
 * - Inside the native mobile shell the create()/get() ceremony itself is
 *   delegated to the app (nativePasskeyBridge) — WKWebView has no WebAuthn.
 *   Everything around it (options, verification, Cognito challenge) is
 *   unchanged.
 */

/** True when this environment can run a WebAuthn ceremony at all. */
export function isPasskeySupported(): boolean {
  // The shell announces native support explicitly. Without that announcement
  // the shell is passkey-less even when WKWebView defines PublicKeyCredential —
  // the ceremony would be blocked by iOS (see SEE-213's useLoginFlow guard,
  // which this check replaces).
  if (isMobileAppShell()) {
    return isNativePasskeyAvailable();
  }
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

/** The user cancelled / dismissed the native passkey sheet. */
export function isUserCancellation(error: unknown): boolean {
  return error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

/**
 * Create a passkey for the signed-in user: fetch creation options, invoke the
 * native OS sheet via navigator.credentials.create(), then verify + store the
 * public key server-side. Returns the created passkey's label on success.
 */
export async function registerPasskey(name?: string): Promise<{ verified: boolean; name: string | null }> {
  const { optionsJson } = await webAuthnService.startPasskeyRegistration();
  const optionsJSON = JSON.parse(optionsJson);
  const attestation: RegistrationResponseJSON = isNativePasskeyAvailable()
    ? await runNativePasskeyCeremony<RegistrationResponseJSON>('create', optionsJSON)
    : await startRegistration({ optionsJSON });
  const result = await webAuthnService.verifyPasskeyRegistration(JSON.stringify(attestation), name);
  return { verified: result.verified, name: result.passkey?.name ?? name ?? null };
}

/** Raised when the account has no passkey / can't do a passkey challenge. */
export const NO_PASSKEY = 'NO_PASSKEY';

/**
 * Sign in with a passkey via a Cognito CUSTOM_AUTH challenge. `username` is the
 * Cognito username (resolve the email first with resolveEmailToUsername);
 * `sessionId` is the browser's SESSION_ID_KEY value, passed to Cognito as
 * clientMetadata.session_id like the password flow does.
 *
 * Amplify v4's Auth.signIn(username) with NO password initiates the CUSTOM_AUTH
 * flow; our CreateAuthChallenge trigger returns the WebAuthn request options in
 * challengeParam.publicKeyJson, we sign them with the authenticator, and answer
 * the challenge. On success Amplify stores the tokens (Auth.currentSession()
 * then works). Rejects with NO_PASSKEY when the user has no credential, or the
 * native error on cancel/failure — the caller falls back to the password step.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function authenticateWithPasskey(username: string, sessionId: string): Promise<any> {
  // SEE-1265: PreTokenGeneration only rotates and creates the caller's
  // SelectedPaidolSession row when the login carries `session_id` in
  // clientMetadata, exactly as the password flow does. Without it a passkey
  // login has no server-side session row, `createOrGetPaidolSession` answers
  // 401 for the browser's session id, and the first company switch signs the
  // user out. Cognito forwards RespondToAuthChallenge metadata to
  // PreTokenGeneration, so it must ride on the challenge answer; it is sent on
  // InitiateAuth too so the two calls of one ceremony carry the same session.
  const clientMetadata = { session_id: sessionId };
  // No password → Amplify uses the CUSTOM_AUTH flow (requires ALLOW_CUSTOM_AUTH
  // on the app client, wired by the Amplify auth override).
  const cognitoUser = await Auth.signIn(username, undefined, clientMetadata);

  if (cognitoUser?.challengeName !== 'CUSTOM_CHALLENGE') {
    throw new Error(NO_PASSKEY);
  }
  const optionsJson: string | undefined = cognitoUser.challengeParam?.publicKeyJson;
  if (!optionsJson) {
    throw new Error(NO_PASSKEY);
  }

  const optionsJSON = JSON.parse(optionsJson);
  // Defense in depth: the backend refuses to issue a challenge for a user with
  // no credentials, but if an empty allowCredentials list ever got through, the
  // browser would treat it as a discoverable-credential request and pop the
  // passkey picker at every password user. Bail to the password step instead.
  if (!optionsJSON.allowCredentials?.length) {
    throw new Error(NO_PASSKEY);
  }

  const assertion: AuthenticationResponseJSON = isNativePasskeyAvailable()
    ? await runNativePasskeyCeremony<AuthenticationResponseJSON>('get', optionsJSON)
    : await startAuthentication({ optionsJSON });

  return Auth.sendCustomChallengeAnswer(cognitoUser, JSON.stringify(assertion), clientMetadata);
}
